import React, { useEffect, useRef } from "react";
import { awsIcon, initEstimate } from "./helpers";

export function TabPanelCode({ className = "" }) {
  return (
    <div className={`tp tp-code ${className}`.trim()}>
      <div className="tp-topbar">
        <div className="tp-breadcrumb">
          <span className="tp-folder">▸</span>
          <span>platform</span>
          <span className="tp-sep">/</span>
          <span>main.tf</span>
          <span className="tp-branch">branch: platform/refactor</span>
        </div>
        <div className="tp-actions">
          <span className="tp-status is-ok">● synced with canvas</span>
        </div>
      </div>
      <div className="tp-body tp-code-body">
        <aside className="tp-tree">
          <div className="tp-tree-group">infrastructure</div>
          <div className="tp-tree-item">▾ main.tf</div>
          <div className="tp-tree-item is-active">&nbsp;&nbsp;resources.tf</div>
          <div className="tp-tree-item">&nbsp;&nbsp;variables.tf</div>
          <div className="tp-tree-item">&nbsp;&nbsp;outputs.tf</div>
          <div className="tp-tree-group">stacks</div>
          <div className="tp-tree-item">▸ data-lake</div>
          <div className="tp-tree-item">▸ networking</div>
        </aside>
        <pre className="tp-editor"><code>
<span className="cl-c"># Generated from canvas · last edit 2s ago</span>
{"\n\n"}
<span className="cl-k">resource</span> <span className="cl-s">"aws_lb"</span> <span className="cl-s">"alb"</span> {"{"}
{"\n  "}<span className="cl-p">name</span>               = <span className="cl-s">"ib-alb"</span>
{"\n  "}<span className="cl-p">load_balancer_type</span> = <span className="cl-s">"application"</span>
{"\n  "}<span className="cl-p">subnets</span>            = <span className="cl-v">module.networking.public_subnets</span>
{"\n  "}<span className="cl-p">security_groups</span>    = [<span className="cl-v">aws_security_group.web.id</span>]
{"\n"}<span className="cl-add">+  tags = local.default_tags</span>
{"\n"}{"}"}
{"\n\n"}
<span className="cl-k">resource</span> <span className="cl-s">"aws_instance"</span> <span className="cl-s">"ib_test_server"</span> {"{"}
{"\n  "}<span className="cl-p">ami</span>                    = <span className="cl-v">data.aws_ami.amazon_linux.id</span>
{"\n  "}<span className="cl-p">instance_type</span>          = <span className="cl-s">"t3.small"</span>
{"\n  "}<span className="cl-p">subnet_id</span>              = <span className="cl-v">module.networking.private_subnets[0]</span>
{"\n  "}<span className="cl-p">vpc_security_group_ids</span> = [<span className="cl-v">aws_security_group.app.id</span>]
{"\n"}<span className="cl-add">+  tags = merge(local.default_tags, {"{"} role = "app" {"}"})</span>
{"\n"}{"}"}
        </code></pre>
      </div>
      <footer className="tp-footer">
        <span className="tp-pill is-ok">✓ terraform validate passed</span>
        <span className="tp-pill">± 2 files changed · +14 / −3</span>
        <span className="tp-flex"></span>
        <span className="tp-mono">canvas ↔ main branch in sync</span>
      </footer>
    </div>
  );
}

export function TabPanelEstimate({ className = "" }) {
  const rootRef = useRef(null);
  useEffect(() => {
    if (!rootRef.current) return undefined;
    return initEstimate(rootRef.current);
  }, []);

  // The template mirrors the vanilla prototype verbatim so the imperative
  // init code in `initEstimate` can find its data-k selectors.
  const html = `
    <div class="tp-topbar">
      <div class="tp-breadcrumb">
        <span>Cost breakdown</span>
        <span class="tp-sep">·</span>
        <span>us-east-1</span>
        <span class="tp-branch">commit plans · Savings 1yr partial</span>
      </div>
      <div class="tp-actions">
        <span class="tp-status is-ok">● live estimate</span>
      </div>
    </div>
    <div class="tp-body tp-est-body">
      <div class="tp-est-summary">
        <div class="tp-est-big">
          <span class="tp-est-label">Projected monthly</span>
          <span class="tp-est-value" data-k="total">$0.00<span class="tp-est-unit">/mo</span></span>
          <span class="tp-est-delta is-up">▲ live · changes as you tune</span>
        </div>
        <div class="tp-est-mini"><span>Direct</span><b data-k="direct">$0.00</b></div>
        <div class="tp-est-mini"><span>Implied</span><b class="is-implied" data-k="implied">$0.00</b></div>
        <div class="tp-est-mini"><span>Annual</span><b data-k="annual">$0.00</b></div>
      </div>
      <div class="tp-est-table">
        <div class="tp-row tp-row-head">
          <span></span>
          <span>Resource</span>
          <span>Direct</span>
          <span>Implied</span>
          <span>Total</span>
        </div>

        <div class="tp-row">
          <span class="tp-row-ic">${awsIcon('alb', 22)}</span>
          <span class="tp-row-name"><b>aws_lb.alb</b><span class="tp-row-kind">Application LB</span></span>
          <span class="tp-row-cell">$21.60</span>
          <span class="tp-row-cell is-implied">+$0.32 cross-AZ</span>
          <span class="tp-row-cell is-total">$21.92</span>
        </div>

        <div class="tp-row tp-row-tunable" data-tune="ec2">
          <span class="tp-row-ic">${awsIcon('ec2', 22)}</span>
          <span class="tp-row-name">
            <b>aws_instance.ib_test_server</b>
            <span class="tp-row-kind">
              <span class="tp-knob tp-knob-select">
                <select data-k="ec2Type" aria-label="EC2 instance type">
                  <option value="t3.medium">t3.medium · $15.18</option>
                  <option value="t3.large">t3.large · $27.61</option>
                </select>
                <span class="tp-knob-chev">▾</span>
              </span>
              · 730h
            </span>
          </span>
          <span class="tp-row-cell" data-k="ec2Direct">$0.00</span>
          <span class="tp-row-cell is-implied">+$0.00</span>
          <span class="tp-row-cell is-total" data-k="ec2Total">$0.00</span>
        </div>

        <div class="tp-row tp-row-tunable" data-tune="s3">
          <span class="tp-row-ic">${awsIcon('s3', 22)}</span>
          <span class="tp-row-name">
            <b>aws_s3_bucket.artifacts</b>
            <span class="tp-row-kind">
              S3 Standard ·
              <span class="tp-knob tp-knob-slider">
                <input type="range" min="0" max="3" step="1" value="0" data-k="s3Step" aria-label="S3 storage size">
                <span class="tp-knob-val" data-k="s3Label">10 GB</span>
              </span>
            </span>
          </span>
          <span class="tp-row-cell" data-k="s3Direct">$0.00</span>
          <span class="tp-row-cell is-implied">+$0.18 egress</span>
          <span class="tp-row-cell is-total" data-k="s3Total">$0.00</span>
        </div>

        <div class="tp-row">
          <span class="tp-row-ic">${awsIcon('rds', 22)}</span>
          <span class="tp-row-name"><b>aws_db_instance.primary</b><span class="tp-row-kind">db.t3.micro · multi-AZ</span></span>
          <span class="tp-row-cell">$28.50</span>
          <span class="tp-row-cell is-implied">+$0.00</span>
          <span class="tp-row-cell is-total">$28.50</span>
        </div>

        <div class="tp-row tp-row-tunable" data-tune="lambda">
          <span class="tp-row-ic">${awsIcon('lambda', 22)}</span>
          <span class="tp-row-name">
            <b>aws_lambda.processor</b>
            <span class="tp-row-kind">
              <span class="tp-knob tp-knob-stepper">
                <button data-k="lambdaPrev" aria-label="Fewer invocations">−</button>
                <span class="tp-knob-val" data-k="lambdaLabel">1M</span>
                <button data-k="lambdaNext" aria-label="More invocations">+</button>
              </span>
              invocations
            </span>
          </span>
          <span class="tp-row-cell" data-k="lambdaDirect">$0.00</span>
          <span class="tp-row-cell is-implied">+$0.03 data transfer</span>
          <span class="tp-row-cell is-total" data-k="lambdaTotal">$0.00</span>
        </div>
      </div>
    </div>
    <footer class="tp-footer">
      <span class="tp-pill is-ok" data-k="ceiling">✓ under $100/mo ceiling</span>
      <span class="tp-pill is-warn">⚠ 1 implied cost: cross-AZ traffic</span>
      <span class="tp-flex"></span>
      <span class="tp-mono">5 resources · RI-optimized</span>
    </footer>
  `;

  return (
    <div
      ref={rootRef}
      className={`tp tp-estimate ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function TabPanelReview({ className = "" }) {
  return (
    <div className={`tp tp-review ${className}`.trim()}>
      <div className="tp-topbar">
        <div className="tp-breadcrumb">
          <span className="tp-pr">#482</span>
          <span>chore(platform): add ALB + app tier</span>
          <span className="tp-branch">platform/refactor → main</span>
        </div>
        <div className="tp-actions">
          <span className="tp-status is-ok">● ready to merge</span>
        </div>
      </div>
      <div className="tp-body tp-rev-body">
        <section className="tp-rev-col">
          <h5 className="tp-rev-h">Policy checks <span className="tp-rev-h-count">5 passed</span></h5>
          <div className="tp-check is-pass"><span className="tp-check-ic">✓</span><div><b>tagging.required</b><span>owner, env, cost-center present</span></div></div>
          <div className="tp-check is-pass"><span className="tp-check-ic">✓</span><div><b>network.no-public-rds</b><span>RDS in private subnets only</span></div></div>
          <div className="tp-check is-pass"><span className="tp-check-ic">✓</span><div><b>iam.least-priv</b><span>No wildcard actions on new roles</span></div></div>
          <div className="tp-check is-pass"><span className="tp-check-ic">✓</span><div><b>cost.ceiling</b><span>$66.29 &lt; $100.00 monthly cap</span></div></div>
          <div className="tp-check is-pass"><span className="tp-check-ic">✓</span><div><b>naming.convention</b><span>Prefix <code>ib-</code> enforced</span></div></div>
        </section>
        <section className="tp-rev-col">
          <h5 className="tp-rev-h">Plan preview <span className="tp-rev-h-count">2 add · 1 change</span></h5>
          <pre className="tp-plan"><code>
<span className="cl-add">+ aws_lb.alb</span>{"                       "}<span className="cl-c"># create</span>
{"\n"}<span className="cl-add">+ aws_instance.ib_test_server</span>{"      "}<span className="cl-c"># create</span>
{"\n"}<span className="cl-c">~ aws_security_group.web</span>{"          "}<span className="cl-c"># ingress → 80/443</span>
{"\n\n"}<span className="cl-c">Plan: 2 to add, 1 to change, 0 to destroy.</span>
{"\n"}<span className="cl-c">Cost:  +$37.10 / mo   (within budget)</span>
          </code></pre>
          <div className="tp-rev-approvals">
            <span className="tp-avatar" style={{ background: "#EDA400" }}>M</span>
            <span className="tp-avatar" style={{ background: "#19A58A" }}>D</span>
            <span className="tp-avatar" style={{ background: "#8C4FFF" }}>S</span>
            <span className="tp-rev-meta">3 reviewers · 2 approved</span>
          </div>
        </section>
      </div>
      <footer className="tp-footer">
        <span className="tp-pill is-ok">✓ 5/5 policies</span>
        <span className="tp-pill is-ok">✓ plan clean</span>
        <span className="tp-pill">+ $1.12 /mo</span>
        <span className="tp-flex"></span>
        <button className="tp-merge">Merge & apply →</button>
      </footer>
    </div>
  );
}
